import {
  getV1CompaniesCompanyIdUsers,
  patchV1CompaniesCompanyIdUsersId,
  postV1CompaniesCompanyIdUsers,
  TimeZone,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';

import { User } from '~hooks/useUsers/models';
import { CSVColumn } from '~utils/csv/csvProcessor';
import { fetchAllApiPages } from '~utils/fetchAllPages';
import { toE164PhoneNumber } from '~utils/utilFunctions';

import { getPhoneValidationErrors } from './phoneValidationErrors';
import { EntityCSVConfig } from './types';

const buildUsersColumns = (validRoles: string[]): CSVColumn[] => [
  {
    key: 'id',
    header: 'id',
    required: false,
    exampleValue: '',
    description: 'Existing user ID (for updates only)',
  },
  {
    key: 'first_name',
    header: 'first_name',
    required: true,
    exampleValue: 'John',
    description: 'First name',
  },
  {
    key: 'last_name',
    header: 'last_name',
    required: true,
    exampleValue: 'Doe',
    description: 'Last name',
  },
  {
    key: 'email',
    header: 'email',
    required: false,
    exampleValue: 'john.doe@example.com',
    description: 'Email (required if no phone)',
  },
  {
    key: 'phone',
    header: 'phone',
    required: false,
    exampleValue: '+14155551234',
    description:
      'US numbers like 555-555-5555 or (555) 555-5555; other countries start with + and the country code (e.g., +14155551234) — required if no email',
  },
  {
    key: 'role',
    header: 'role',
    required: true,
    enumValues: validRoles,
    isMultipleEnum: true,
    exampleValue: 'DRIVER',
    description: 'System role (can be comma-separated)',
  },
  {
    key: 'time_zone',
    header: 'time_zone',
    required: true,
    exampleValue: 'America/New_York',
    description: 'IANA timezone (see reference below)',
  },
  {
    key: 'external_id',
    header: 'external_id',
    required: false,
    exampleValue: 'USR-001',
    description: 'Your internal reference ID',
  },
  {
    key: 'status',
    header: 'status',
    required: false,
    enumValues: ['ACTIVE', 'INACTIVE'],
    exampleValue: 'ACTIVE',
    description: 'ACTIVE or INACTIVE',
  },
  {
    key: 'notes',
    header: 'notes',
    required: false,
    exampleValue: '',
    description: 'Driver notes (max 350 chars)',
  },
  {
    key: 'address',
    header: 'address',
    required: false,
    exampleValue: '',
    description: 'Driver home address',
  },
];

export const createUsersConfig = (
  getRoleIdByName: (name: string) => string | undefined,
  validRoles: string[],
): EntityCSVConfig<User> => ({
  entityType: 'users',
  columns: buildUsersColumns(validRoles),

  fetchAll: (companyId: string) =>
    fetchAllApiPages(
      (query) =>
        getV1CompaniesCompanyIdUsers({ path: { 'company-id': companyId }, query }),
      User.parse,
    ),

  create: async (companyId: string, data: Record<string, string>): Promise<User> => {
    const roles = data.role
      ?.split(',')
      .map((r) => r.trim().toUpperCase())
      .filter(Boolean);
    const roleIds = roles
      ?.map((role) => getRoleIdByName(role))
      .filter(Boolean) as string[];

    const response = await postV1CompaniesCompanyIdUsers({
      path: { 'company-id': companyId },
      body: {
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email || undefined,
        phone: toE164PhoneNumber(data.phone),
        time_zone: data.time_zone as TimeZone,
        external_id: data.external_id || undefined,
        iam_role_ids: roleIds,
        send_invite: {
          email: Boolean(data.email),
          sms: Boolean(data.phone && !data.email),
        },
      },
    });

    return User.parse(response.data.data);
  },

  update: async (
    id: string,
    data: Record<string, string>,
    companyId: string,
  ): Promise<User> => {
    const roles = data.role
      ?.split(',')
      .map((r) => r.trim().toUpperCase())
      .filter(Boolean);
    const roleIds = roles
      ?.map((role) => getRoleIdByName(role))
      .filter(Boolean) as string[];

    const response = await patchV1CompaniesCompanyIdUsersId({
      path: { 'company-id': companyId, id },
      body: {
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email || undefined,
        phone: toE164PhoneNumber(data.phone),
        time_zone: data.time_zone as TimeZone,
        external_id: data.external_id || undefined,
        iam_role_ids: roleIds,
      },
    });

    return User.parse(response.data.data);
  },

  transformExport: (user: User): Record<string, string> => ({
    id: user.id,
    first_name: user.firstName,
    last_name: user.lastName,
    email: user.email || '',
    phone: user.phone || '',
    role: user.userRoles?.map((r) => r.name.toUpperCase()).join(', ') || '',
    time_zone: user.timeZone || '',
    external_id: user.externalId || '',
    status: user.isActiveMember || user.isActiveUser ? 'ACTIVE' : 'INACTIVE',
    notes: '',
    address: '',
  }),

  transformImport: (row: Record<string, string>): Record<string, unknown> => ({
    first_name: row.first_name?.trim(),
    last_name: row.last_name?.trim(),
    email: row.email?.trim() || null,
    phone: row.phone?.trim() || null,
    role: row.role?.trim(),
    time_zone: row.time_zone?.trim(),
    external_id: row.external_id?.trim() || null,
  }),

  validateRow: (row: Record<string, string>): string[] => {
    const errors: string[] = [];

    if (!row.first_name?.trim()) {
      errors.push(t('csv_import_export.errors.required_field', { field: 'first_name' }));
    }
    if (!row.last_name?.trim()) {
      errors.push(t('csv_import_export.errors.required_field', { field: 'last_name' }));
    }
    if (!row.email?.trim() && !row.phone?.trim()) {
      errors.push(t('csv_import_export.errors.email_or_phone_required'));
    }
    errors.push(...getPhoneValidationErrors(row.phone, 'phone'));
    if (!row.role?.trim()) {
      errors.push(t('csv_import_export.errors.required_field', { field: 'role' }));
    } else if (validRoles.length > 0) {
      const roles = row.role.split(',').map((r) => r.trim().toUpperCase());
      const invalidRoles = roles.filter((r) => !validRoles.includes(r));
      if (invalidRoles.length > 0) {
        errors.push(
          t('csv_import_export.errors.invalid_enum', {
            field: 'role',
            values: validRoles.join(', '),
          }),
        );
      }
    }
    if (!row.time_zone?.trim()) {
      errors.push(t('csv_import_export.errors.required_field', { field: 'time_zone' }));
    }
    if (row.notes && row.notes.length > 350) {
      errors.push(t('csv_import_export.errors.number_max', { field: 'notes', max: 350 }));
    }

    return errors;
  },
});
